<template>
  <img
    :src="logoSrc"
    :alt="alt"
    class="ram-brand-logo"
    :class="`ram-brand-logo--${variant}`"
    :width="computedWidth"
    :height="height"
    :style="{ width: `${computedWidth}px`, height: `${height}px` }"
    draggable="false"
  />
</template>

<script setup lang="ts">
/**
 * Brand wordmark — the full "Rafid Auto-Maintenance" bilingual lockup
 * supplied by the stakeholder.
 *
 * Sizing strategy:
 *   - Both `width` and `height` are set as HTML attributes AND as inline
 *     CSS, with `width` computed from the natural aspect ratio
 *     (1650 / 659 ≈ 2.504). This pins the box exactly so neither
 *     Tailwind's preflight (`height: auto`) nor a parent's `max-width`
 *     can stretch the image off-aspect.
 *
 * Variants:
 *   - `auto`  (default) — dark navy on light surfaces; flipped to white
 *                          via CSS filter when `[data-theme="dark"]` is
 *                          set on `<html>`.
 *   - `light` — always rendered as white silhouette + cyan glow, for
 *               fixed dark backgrounds (auth-shell brand panel).
 *   - `dark`  — always rendered in original navy + cyan; for fixed
 *               light backgrounds.
 *
 * Tracked in docs/deferred.md: when stakeholder ships proper light/dark
 * SVG variants, swap the asset import + drop the CSS filter trick.
 */
import { computed } from "vue";
import logoSrc from "../../assets/brand/ram-logo.png";

// Intrinsic dimensions of the supplied PNG (1650 × 659).
const ASPECT = 1650 / 659;

const props = withDefaults(
  defineProps<{
    height?: number;
    variant?: "auto" | "light" | "dark";
    alt?: string;
  }>(),
  {
    height: 32,
    variant: "auto",
    alt: "Rafid Auto-Maintenance",
  },
);

const computedWidth = computed(() => Math.round(props.height * ASPECT));
</script>

<style scoped>
.ram-brand-logo {
  display: inline-block;
  flex: none;
  vertical-align: middle;
  user-select: none;
  -webkit-user-drag: none;
  /* Belt-and-braces: even if a parent applies `max-width: 100%`, the
     intrinsic aspect ratio is preserved by the box, not by stretching
     the bitmap. */
  object-fit: contain;
}

/* `auto` — flip in dark mode. The selector targets <html data-theme="dark">
   which sits above the scoped component, so Vue's scoping doesn't break it. */
[data-theme="dark"] .ram-brand-logo--auto {
  filter: brightness(0) invert(1) opacity(0.92);
}

/* `light` — always white silhouette regardless of theme. The cyan
   drop-shadow gives a soft glow on dark surfaces (auth-shell brand
   panel). The order matters: brightness(0)+invert(1) flips the logo
   to white first, then the shadow is computed against that white
   silhouette and rendered in cyan. */
.ram-brand-logo--light {
  filter:
    brightness(0)
    invert(1)
    drop-shadow(0 4px 18px rgba(6, 182, 212, 0.35));
}
</style>
